// 환경변수 타입 정의
export interface AppConfig {
  apiBaseUrl: string;
  mediaBaseUrl: string;
  thumbnailBaseUrl: string;
  healthCheckUrl: string;
}

// 환경변수 검증 함수
function validateEnvVar(value: string | undefined, name: string): string {
  if (!value) {
    console.warn(`Environment variable ${name} is not defined, using fallback`);
    return '';
  }
  return value;
}

// 런타임 호스트 기반 API 베이스 URL 결정.
// - https://bus.forworld.kr  → https://busback.forworld.kr (nginx 프록시)
// - http://bus.forworld.kr:3000 → http://bus.forworld.kr:3001 (기존 직접 접속)
// - 그 외(localhost 등) → 환경변수 VITE_API_BASE_URL 사용 (개발 환경)
function resolveApiBaseUrl(): string {
  if (typeof window !== 'undefined') {
    const { protocol, hostname } = window.location;
    if (hostname === 'bus.forworld.kr') {
      return protocol === 'https:'
        ? 'https://busback.forworld.kr'
        : 'http://bus.forworld.kr:3001';
    }
  }
  return validateEnvVar(import.meta.env.VITE_API_BASE_URL, 'VITE_API_BASE_URL') || 'http://localhost:3001';
}

// 환경변수에서 설정 가져오기
function getConfig(): AppConfig {
  const apiBaseUrl = resolveApiBaseUrl();

  return {
    apiBaseUrl,
    mediaBaseUrl: `${apiBaseUrl}/uploads`,
    thumbnailBaseUrl: `${apiBaseUrl}/uploads/thumbnails`,
    healthCheckUrl: `${apiBaseUrl}/health`
  };
}

// 전역 설정 객체
export const config: AppConfig = getConfig();

// 편의 함수들
export const getApiUrl = (endpoint: string): string => {
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${config.apiBaseUrl}${cleanEndpoint}`;
};

export const getMediaUrl = (filePath: string): string => {
  if (!filePath) return '';

  // 이미 전체 URL인 경우 그대로 반환
  if (filePath.startsWith('http')) return filePath;

  // 경로 정규화
  const cleanPath = filePath.startsWith('/') ? filePath.slice(1) : filePath;
  return `${config.mediaBaseUrl}/${cleanPath}`;
};

export const getThumbnailUrl = (filename: string, companyId?: number): string => {
  if (!filename) return '';

  const thumbFilename = `thumb-${filename.replace(/\.[^/.]+$/, '.jpg')}`;

  if (companyId !== undefined && companyId !== null) {
    return `${config.thumbnailBaseUrl}/company_${companyId}/${thumbFilename}`;
  }

  return `${config.thumbnailBaseUrl}/${thumbFilename}`;
};

export const getCompanyMediaUrl = (filename: string, companyId?: number): string => {
  if (!filename) return '';

  if (companyId !== undefined && companyId !== null) {
    return `${config.mediaBaseUrl}/media/company_${companyId}/${filename}`;
  }

  return `${config.mediaBaseUrl}/media/${filename}`;
};

// 미디어 URL 생성 시 fallback 처리
export const createMediaUrlWithFallback = (filename: string, companyId?: number): string[] => {
  if (!filename) return [];

  const urls: string[] = [];

  // Company specific path 우선 (companyId가 0일 때도 포함)
  if (companyId !== undefined && companyId !== null) {
    urls.push(getCompanyMediaUrl(filename, companyId));
  }

  // 기본 미디어 경로 (fallback)
  urls.push(getMediaUrl(`media/${filename}`));

  return urls;
};

// 개발 모드 확인
export const isDevelopment = import.meta.env.MODE === 'development';
export const isProduction = import.meta.env.MODE === 'production';

// 디버그 로깅
if (isDevelopment) {
  console.log('[Config] 환경 설정:', {
    mode: import.meta.env.MODE,
    config
  });
}